import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';

import Reviews from '../components/reviews/Reviews';
import MoverProfileModal from '../components/dashboard/MoverProfileModal';
import BusinessProfileEditable from '../components/dashboard/BusinessProfileEditable';
import MyTeam from '../components/dashboard/MyTeam';
import quoteService from '../services/quoteService';
import { checkMoverStatus } from '../services/moverAuthService';
import supabase from '../lib/supabaseClient';

const MOVER_DASHBOARD_TAB_STORAGE_KEY = 'moverDashboardActiveTab';
const MOVER_DASHBOARD_TAB_IDS = new Set(['overview', 'leads', 'business', 'reviews', 'team']);
const TEAM_MEMBER_TAB_IDS = new Set(['overview', 'leads']);
const PROFILE_COMPLETION_TOTAL = 11;

// Helper function to calculate distance between two ZIP codes using Haversine formula
const calculateZipDistance = async (zip1, zip2) => {
    if (!zip1 || !zip2) return null;

    try {
        // Fetch coordinates for both ZIP codes using zippopotam.us API
        const [coords1, coords2] = await Promise.all([
            fetch(`https://api.zippopotam.us/us/${zip1}`).then(r => r.ok ? r.json() : null),
            fetch(`https://api.zippopotam.us/us/${zip2}`).then(r => r.ok ? r.json() : null)
        ]);

        if (!coords1?.places?.[0] || !coords2?.places?.[0]) return null;

        const lat1 = parseFloat(coords1.places[0].latitude);
        const lon1 = parseFloat(coords1.places[0].longitude);
        const lat2 = parseFloat(coords2.places[0].latitude);
        const lon2 = parseFloat(coords2.places[0].longitude);

        // Haversine formula
        const R = 3959; // Earth's radius in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        return Math.round(distance);
    } catch (error) {
        return null;
    }
};

const Dashboard = () => {
    const [activeTab, setActiveTab] = useState(() => {
        if (typeof window === 'undefined') return 'overview';

        const savedTab = localStorage.getItem(MOVER_DASHBOARD_TAB_STORAGE_KEY);
        return MOVER_DASHBOARD_TAB_IDS.has(savedTab) ? savedTab : 'overview';
    });
    const [selectedLead, setSelectedLead] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState([]);
    // Until admin verifies the account, leads should be locked
    const [isVerified, setIsVerified] = useState(false);
    const [isProfileComplete, setIsProfileComplete] = useState(false);
    const [completionPercentage, setCompletionPercentage] = useState(0);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [moverData, setMoverData] = useState(null);
    const [isTeamMember, setIsTeamMember] = useState(false);
    const [recentReviews, setRecentReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({ averageRating: 0, totalReviews: 0 });
    const [teamMembers, setTeamMembers] = useState([]);
    const [showPauseModal, setShowPauseModal] = useState(false);
    const [isPausing, setIsPausing] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [profile, setProfile] = useState({
        business: {
            company: 'Premier Moving Co.',
            logo: '/premier-moving-logo.png',
            email: 'contact@premiermove.com',
            phone: '+1 (555) 123-4567',
            website: 'https://www.premiermove.com',
            license: 'BL-CA-2025-9876',
            address: '123 Business St',
            city: 'San Francisco',
            state: 'CA',
            zip: '94102',
            employees: '15-25 employees',
            years: '12 years',
            description: 'Premier Moving Co. has been serving the Bay Area for over 12 years with professional, reliable moving services. We specialize in both residential and commercial moves with a focus on customer satisfaction.',
            serviceAreas: ['San Francisco Bay Area', 'Northern California', 'Interstate (West Coast)'],
            specialties: ['Residential', 'Commercial', 'Long Distance', 'Piano Moving']
        },
        insurance: {
            dot: 'DOT-123456',
            mc: 'MC-789012',
            provider: 'Commercial Insurance Group',
            policy: 'POL-2025-4567',
            liability: '$1,000,000',
            cargo: '$250,000',
            expiry: '2026-12-31'
        }
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(MOVER_DASHBOARD_TAB_STORAGE_KEY, activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (isTeamMember && !TEAM_MEMBER_TAB_IDS.has(activeTab)) {
            setActiveTab('overview');
        }
    }, [isTeamMember, activeTab]);

    const calculateCompletionPercentage = ({ mover, hasApprovedInsurance, hasApprovedLicense }) => {
        if (!mover) return 0;

        let completed = 0;

        if (mover.company_name) completed++;
        if (mover.company_description) completed++;
        if (mover.website_link) completed++;
        if (mover.company_logo) completed++;
        if (mover.google_profile_link) completed++;
        if (mover.phone) completed++;
        if (mover.email) completed++;
        if (mover.business_address) completed++;
        if (hasApprovedInsurance) completed++;
        if (hasApprovedLicense) completed++;
        if (mover.city && mover.state) completed++;

        return Math.round((completed / PROFILE_COMPLETION_TOTAL) * 100);
    };

    // Function to check profile completion
    const checkProfileCompletion = async (mover) => {
        if (!mover?.id) return;

        try {
            // Fetch ONLY approved insurance and licenses for completion check
            const [{ count: insuranceCount, error: insuranceError }, { count: licenseCount, error: licenseError }] = await Promise.all([
                supabase
                    .from('mover_insurance')
                    .select('*', { count: 'exact', head: true })
                    .eq('mover_id', mover.id)
                    .eq('status', 'approved'),
                supabase
                    .from('mover_licenses')
                    .select('*', { count: 'exact', head: true })
                    .eq('mover_id', mover.id)
                    .eq('status', 'approved')
            ]);

            if (insuranceError) throw insuranceError;
            if (licenseError) throw licenseError;

            const percentage = calculateCompletionPercentage({
                mover,
                hasApprovedInsurance: (insuranceCount || 0) > 0,
                hasApprovedLicense: (licenseCount || 0) > 0
            });

            setCompletionPercentage(percentage);
            setIsProfileComplete(percentage === 100);
        } catch {
            // Keep existing value if completion check fails
        }
    };

    // Function to toggle account pause status
    const handlePauseAccount = async () => {
        if (!moverData?.id) return;

        setIsPausing(true);
        try {
            const newPausedStatus = !moverData.account_paused;
            
            const { error } = await supabase
                .from('movers_data')
                .update({ account_paused: newPausedStatus })
                .eq('id', moverData.id);

            if (error) throw error;

            // Send email notification
            try {
                // In production (deployed on Vercel), use relative path (same domain)
                // In development, use localhost server
                const API_URL = window.location.hostname === 'localhost' 
                    ? 'http://localhost:3001' 
                    : '';
                const endpoint = newPausedStatus 
                    ? '/api/send-account-paused-email' 
                    : '/api/send-account-resumed-email';
                
                await fetch(`${API_URL}${endpoint}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: moverData.email,
                        name: moverData.company_name || 'Valued Mover'
                    })
                });
            } catch (emailError) {
                // Don't fail the operation if email fails
            }

            // Update local state
            setMoverData(prev => ({ ...prev, account_paused: newPausedStatus }));
            setShowPauseModal(false);
            
            // Show success popup
            setShowSuccessPopup(true);
            setTimeout(() => setShowSuccessPopup(false), 3000);
        } catch (error) {
            setShowPauseModal(false);
        } finally {
            setIsPausing(false);
        }
    };

    // Load mover data and leads on component mount
    useEffect(() => {
        const loadMoverData = async () => {
            try {
                setLoading(true);

                // Check if user is a team member
                const isTeam = sessionStorage.getItem('isTeamMember') === 'true';
                setIsTeamMember(isTeam);


                // Get current mover's data
                const { isApproved, moverData: mover, isTeamMember: isTeamFromCheck } = await checkMoverStatus();


                if (isApproved && mover) {
                    setMoverData(mover);

                    // Update profile with real data
                    setProfile(prev => ({
                        ...prev,
                        business: {
                            ...prev.business,
                            company: mover.company_name || prev.business.company,
                            logo: mover.company_logo || prev.business.logo,
                            email: mover.email || prev.business.email,
                            phone: mover.phone || prev.business.phone,
                            website: mover.website || prev.business.website,
                            address: mover.address || prev.business.address,
                            city: mover.city || prev.business.city,
                            state: mover.state || prev.business.state,
                            zip: mover.zip_code || prev.business.zip,
                            description: mover.description || prev.business.description,
                        }
                    }));

                    setIsVerified(true);

                    // Check profile completion
                    await checkProfileCompletion(mover);

                    // Fetch team members if this is a mover (not team member)
                    if (!isTeam) {
                        const { data: teamData, error: teamError } = await supabase
                            .from('team_members')
                            .select('*')
                            .eq('mover_id', mover.id)
                            .eq('is_active', true)
                            .order('first_name');

                        if (!teamError && teamData) {
                            setTeamMembers(teamData);
                        }
                    }

                    // Fetch recent reviews (last 5)
                    const { data: reviewsData, error: reviewsError } = await supabase
                        .from('mover_reviews')
                        .select('*')
                        .eq('mover_id', mover.id)
                        .eq('is_approved', true)
                        .order('created_at', { ascending: false })
                        .limit(5);

                    if (reviewsError) {
                    } else if (reviewsData && reviewsData.length > 0) {
                        setRecentReviews(reviewsData);
                        
                        // Calculate review statistics for the stats card
                        const totalReviews = reviewsData.length;
                        const sumRatings = reviewsData.reduce((sum, review) => sum + review.rating, 0);
                        const averageRating = (sumRatings / totalReviews).toFixed(1);
                        
                        setReviewStats({ averageRating, totalReviews });
                    }

                    // Fetch all leads for this mover (team members see all leads, not filtered)
                    const result = await quoteService.getMoverLeads(mover.id, null);


                    if (result.success && result.leads) {

                        // Transform leads data to match the component's expected format
                        const transformedLeads = await Promise.all(result.leads.map(async (lead, index) => {

                            // Calculate distance
                            const dist = await calculateZipDistance(lead.origin_postal_code, lead.destination_postal_code);

                            return {
                                id: `#${lead.id.substring(0, 8)}`,
                                fullId: lead.id,
                                customer: `${lead.customer_first_name || ''} ${lead.customer_last_name || ''}`.trim() || 'N/A',
                                phone: lead.customer_phone || 'N/A',
                                email: lead.customer_email || 'N/A',
                                moveDate: lead.move_date || 'N/A',
                                receivedDate: formatDate(lead.created_at),
                                location: `${lead.origin_city || ''}, ${lead.origin_state || ''}`.trim(),
                                bedrooms: lead.property_size || lead.assistance_type || 'N/A',
                                status: lead.status || 'Pending',
                                statusColor: getStatusColor(lead.status),
                                moveType: lead.move_type || 'N/A',
                                moveService: lead.assistance_type || 'N/A',
                                fromAddress: `${lead.origin_address || ''}, ${lead.origin_city || ''}, ${lead.origin_state || ''} ${lead.origin_postal_code || ''}`.trim(),
                                toAddress: `${lead.destination_address || ''}, ${lead.destination_city || ''}, ${lead.destination_state || ''} ${lead.destination_postal_code || ''}`.trim(),
                                distance: dist ? `${dist} miles` : 'N/A',
                                items: lead.property_size || 'N/A',
                                description: lead.project_description || 'No description provided',

                                // Property-specific details
                                propertySize: lead.property_size || null,

                                // Vehicle-specific details
                                vehicleType: lead.vehicle_type || null,
                                vehicleMake: lead.vehicle_make || null,
                                vehicleModel: lead.vehicle_model || null,
                                vehicleYear: lead.vehicle_year || null,

                                // Piano-specific details
                                pianoType: lead.piano_type || null,
                                pianoFloors: lead.piano_floors_stairs || null,

                                // Truck-specific details
                                truckSize: lead.truck_size || null,
                                
                                // Team assignment
                                assignedTeamMemberId: lead.assigned_team_member_id || null
                            };
                        }));

                        setLeads(transformedLeads);
                    }
                }
            } catch (error) {
            } finally {
                setLoading(false);
            }
        };

        loadMoverData();

        // Set up real-time subscriptions for leads
        const leadsSubscription = supabase
            .channel('mover_leads_channel')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'leads'
                },
                (payload) => {
                    
                    // Add notification for new lead
                    if (payload.eventType === 'INSERT' && payload.new) {
                        const newNotification = {
                            id: payload.new.id,
                            type: 'new_lead',
                            message: `New lead received: ${payload.new.origin_city || 'Unknown'} → ${payload.new.destination_city || 'Unknown'}`,
                            timestamp: new Date().toISOString(),
                            read: false
                        };
                        setNotifications(prev => [newNotification, ...prev]);
                    }
                    
                    loadMoverData();
                }
            )
            .subscribe();

        // Set up real-time subscription for mover profile updates
        const profileSubscription = supabase
            .channel('mover_profile_channel')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'movers_data'
                },
                async (payload) => {
                    if (payload.new) {
                        setMoverData(payload.new);
                        
                        // Update profile display data
                        setProfile(prev => ({
                            ...prev,
                            business: {
                                ...prev.business,
                                company: payload.new.company_name || prev.business.company,
                                logo: payload.new.company_logo || prev.business.logo,
                                email: payload.new.email || prev.business.email,
                                phone: payload.new.phone || prev.business.phone,
                                website: payload.new.website || prev.business.website,
                                address: payload.new.address || prev.business.address,
                                city: payload.new.city || prev.business.city,
                                state: payload.new.state || prev.business.state,
                                zip: payload.new.zip_code || prev.business.zip,
                                description: payload.new.description || prev.business.description,
                            }
                        }));
                        
                        await checkProfileCompletion(payload.new);
                    }
                }
            )
            .subscribe();

        // Set up real-time subscription for insurance updates
        const insuranceSubscription = supabase
            .channel('mover_insurance_channel')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'mover_insurance'
                },
                async (payload) => {
                    if (moverData) {
                        await checkProfileCompletion(moverData);
                    }
                }
            )
            .subscribe();

        // Set up real-time subscription for license updates
        const licenseSubscription = supabase
            .channel('mover_license_channel')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'mover_licenses'
                },
                async (payload) => {
                    if (moverData) {
                        await checkProfileCompletion(moverData);
                    }
                }
            )
            .subscribe();

        // Auto-refresh leads and notifications every 5 minutes
        const intervalId = setInterval(() => {
            loadMoverData();
        }, 5 * 60 * 1000); // 5 minutes

        // Cleanup subscriptions and interval on unmount
        return () => {
            supabase.removeChannel(leadsSubscription);
            supabase.removeChannel(profileSubscription);
            supabase.removeChannel(insuranceSubscription);
            supabase.removeChannel(licenseSubscription);
            clearInterval(intervalId);
        };
    }, []);

    // Function to assign lead to team member
    const handleAssignLead = async (leadId, teamMemberId) => {
        try {
            const fullLeadId = leadId.startsWith('#') ? leadId.substring(1) : leadId;
            
            const { error } = await supabase
                .from('leads')
                .update({ assigned_team_member_id: teamMemberId || null })
                .ilike('id', `${fullLeadId}%`);

            if (error) throw error;

            // Refresh leads
            if (moverData) {
                const result = await quoteService.getMoverLeads(moverData.id, null);
                if (result.success && result.leads) {
                    const transformedLeads = await Promise.all(result.leads.map(async (lead) => {
                        const dist = await calculateZipDistance(lead.origin_postal_code, lead.destination_postal_code);
                        return {
                            id: `#${lead.id.substring(0, 8)}`,
                            fullId: lead.id,
                            customer: `${lead.customer_first_name || ''} ${lead.customer_last_name || ''}`.trim() || 'N/A',
                            phone: lead.customer_phone || 'N/A',
                            email: lead.customer_email || 'N/A',
                            moveDate: lead.move_date || 'N/A',
                            receivedDate: formatDate(lead.created_at),
                            location: `${lead.origin_city || ''}, ${lead.origin_state || ''}`.trim(),
                            bedrooms: lead.property_size || lead.assistance_type || 'N/A',
                            status: lead.status || 'Pending',
                            statusColor: getStatusColor(lead.status),
                            moveType: lead.move_type || 'N/A',
                            moveService: lead.assistance_type || 'N/A',
                            fromAddress: `${lead.origin_address || ''}, ${lead.origin_city || ''}, ${lead.origin_state || ''} ${lead.origin_postal_code || ''}`.trim(),
                            toAddress: `${lead.destination_address || ''}, ${lead.destination_city || ''}, ${lead.destination_state || ''} ${lead.destination_postal_code || ''}`.trim(),
                            distance: dist ? `${dist} miles` : 'N/A',
                            items: lead.property_size || 'N/A',
                            description: lead.project_description || 'No description provided',
                            propertySize: lead.property_size || null,
                            vehicleType: lead.vehicle_type || null,
                            vehicleMake: lead.vehicle_make || null,
                            vehicleModel: lead.vehicle_model || null,
                            vehicleYear: lead.vehicle_year || null,
                            pianoType: lead.piano_type || null,
                            pianoFloors: lead.piano_floors_stairs || null,
                            truckSize: lead.truck_size || null,
                            assignedTeamMemberId: lead.assigned_team_member_id || null
                        };
                    }));
                    setLeads(transformedLeads);
                }
            }

            toast.success('Lead assigned successfully!');
        } catch (error) {
            toast.error('Failed to assign lead: ' + error.message);
        }
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return date.toLocaleDateString();
    };

    // Helper function to get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'responded':
            case 'contacted':
            case 'completed':
                return 'bg-green-500';
            case 'pending':
                return 'bg-yellow-500';
            case 'assigned':
                return 'bg-blue-500';
            case 'cancelled':
                return 'bg-red-500';
            default:
                return 'bg-gray-500';
        }
    };

    const openLeadDetails = (lead) => {
        setSelectedLead(lead);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedLead(null);
    };

    const stats = [
        {
            title: 'Total Leads',
            value: `${moverData?.consumed_leads || 0}/${moverData?.total_leads || 0}`,
            subtitle: moverData?.total_leads > 0 
                ? `${Math.max(0, (moverData.total_leads - (moverData.consumed_leads || 0)))} remaining` 
                : 'No allocation',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            ),
            iconColor: 'text-green-600',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-100'
        },
        {
            title: 'Total Received',
            value: leads.length.toString(),
            subtitle: 'All time',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            ),
            iconColor: 'text-purple-600',
            bgColor: 'bg-purple-50',
            borderColor: 'border-purple-100'
        },
        {
            title: 'Average Rating',
            value: reviewStats.totalReviews > 0 ? reviewStats.averageRating : 'N/A',
            subtitle: `${reviewStats.totalReviews} ${reviewStats.totalReviews === 1 ? 'review' : 'reviews'}`,
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            ),
            iconColor: 'text-orange-500',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-100'
        }
    ];

    // Leads search (frontend-only)
    const [leadQuery, setLeadQuery] = useState('');
    const filteredLeads = (!leadQuery.trim()) ? leads : leads.filter((l) => {
        const q = leadQuery.trim().toLowerCase();
        return [l.customer, l.id, l.location, l.moveType, l.email]
            .filter(Boolean)
            .some(v => String(v).toLowerCase().includes(q));
    });

    const reviews = [
        {
            id: '#LEAD-4511',
            rating: 5,
            author: 'Sarah Johnson',
            initials: 'SJ',
            bgColor: 'bg-purple-500',
            date: '2025-10-15',
            dateText: '📅 2025-10-15',
            fromLocation: 'San Francisco, CA',
            toLocation: 'Los Angeles, CA',
            bedrooms: '4+ Bedrooms',
            text: 'Exceptional service! The team arrived on time, handled all our belongings with care, and completed the move faster than expected. Highly professional and courteous throughout. Would definitely recommend to anyone looking for reliable movers.',
            helpful: 12,
            hasResponse: true,
            response: 'Thank you so much for the wonderful review, Sarah! It was our pleasure to help with your move.',
            responseDate: '2025-10-16'
        },
        {
            id: '#LEAD-4552',
            rating: 5,
            author: 'Mike Chen',
            initials: 'MC',
            bgColor: 'bg-blue-500',
            date: '2025-10-10',
            dateText: '📅 2025-10-10',
            fromLocation: 'Oakland, CA',
            toLocation: 'Berkeley, CA',
            bedrooms: '2-3 Bedrooms',
            text: 'Great experience moving our 2-bedroom apartment. They were very organized, wrapped everything properly, and nothing was damaged. The quote was accurate and no hidden fees. Thank you!',
            helpful: 8,
            hasResponse: true,
            response: 'We appreciate your business, Mike! Glad everything went smoothly.',
            responseDate: '2025-10-11'
        },
        {
            id: '#LEAD-4485',
            rating: 5,
            author: 'David Thompson',
            initials: 'DT',
            bgColor: 'bg-indigo-500',
            date: '2025-09-28',
            dateText: '📅 2025-09-28',
            fromLocation: 'San Francisco, CA',
            toLocation: 'Los Angeles, CA',
            bedrooms: 'Out of State Move',
            text: 'Moved from SF to LA and everything went smoothly. Communication was excellent throughout the process. Items arrived on time and in perfect condition. Worth every penny!',
            helpful: 15,
            hasResponse: true,
            response: 'Thank you, David! Long distance moves are our specialty. Best wishes in your new location!',
            responseDate: '2025-09-29'
        },
        {
            id: '#LEAD-4472',
            rating: 4,
            author: 'Lisa Anderson',
            initials: 'LA',
            bgColor: 'bg-yellow-500',
            date: '2025-09-20',
            dateText: '📅 2025-09-20',
            fromLocation: 'San Francisco, CA',
            toLocation: 'San Francisco, CA',
            bedrooms: 'Studio',
            text: 'Good service, efficient movers. They were a bit delayed due to traffic but kept me informed. Fair pricing and careful with my furniture.',
            helpful: 3,
            hasResponse: false
        },
        {
            id: '#LEAD-4498',
            rating: 4,
            author: 'Emily Rodriguez',
            initials: 'ER',
            bgColor: 'bg-pink-500',
            date: '2025-10-05',
            dateText: '📅 2025-10-05',
            fromLocation: 'San Jose, CA',
            toLocation: 'Sunnyvale, CA',
            bedrooms: '2-3 Bedrooms',
            text: 'Overall good service. The crew was friendly and worked hard. One item had a minor scratch, but they handled the situation professionally and made it right. Would use again.',
            helpful: 5,
            hasResponse: false
        }
    ];

    // Show loading state while fetching data
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-orange-50 via-yellow-50 to-white">
            <Helmet>
                <title>Mover Dashboard – MyMoveAdvisor</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="container mx-auto px-4 py-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            {/* Mover Image and Name */}
                            <div className="flex items-center gap-3">
                                {moverData?.company_logo ? (
                                    <img 
                                        src={moverData.company_logo} 
                                        alt={moverData.company_name}
                                        className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-gray-200"
                                    />
                                ) : (
                                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-xl">
                                        {moverData?.company_name?.charAt(0) || 'M'}
                                    </div>
                                )}
                                <div>
                                    <h1 className="text-xl md:text-2xl font-bold text-gray-800">{moverData?.company_name || 'Mover Dashboard'}</h1>
                                    <p className="text-xs md:text-sm text-gray-600">Manage your business & leads</p>
                                </div>
                            </div>
                        </div>
                        <div className="relative flex items-center gap-3">
                            {/* Pause Account Button - Hidden for team members */}
                            {!isTeamMember && (
                                <button 
                                    onClick={() => setShowPauseModal(true)}
                                    className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                                    moverData?.account_paused 
                                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                                        : 'bg-orange-600 hover:bg-orange-700 text-white'
                                }`}
                            >
                                {moverData?.account_paused ? (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Resume Account
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Pause Account
                                    </>
                                )}
                            </button>
                            )}

                            <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 rounded-full hover:bg-gray-100 transition-colors">
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                {(!isProfileComplete || notifications.filter(n => !n.read).length > 0) && (
                                    <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                        {notifications.filter(n => !n.read).length || ''}
                                    </span>
                                )}
                            </button>

                            {/* Notifications Dropdown with backdrop */}
                            {showNotifications && (
                                <>
                                    {/* Backdrop to close dropdown */}
                                    <div 
                                        className="fixed inset-0 z-40" 
                                        onClick={() => setShowNotifications(false)}
                                    />
                                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 max-h-96 overflow-y-auto">
                                        <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                                            <h3 className="font-semibold text-gray-800">Notifications</h3>
                                            {notifications.length > 0 && (
                                                <button 
                                                    onClick={() => {
                                                        setNotifications(notifications.map(n => ({...n, read: true})));
                                                    }}
                                                    className="text-xs text-blue-600 hover:text-blue-800"
                                                >
                                                    Mark all read
                                                </button>
                                            )}
                                        </div>
                                        <div className="p-2 space-y-2">
                                            {/* Profile completion notification */}
                                            {!isProfileComplete ? (
                                                <div className="p-3 rounded-lg border bg-red-50 border-red-200 text-red-800 text-sm">
                                                    <div className="font-medium">Profile Incomplete</div>
                                                    <div className="text-xs mt-1">Your profile is {completionPercentage}% complete. Complete it to unlock lead details.</div>
                                                </div>
                                            ) : (
                                                <div className="p-3 rounded-lg border bg-green-50 border-green-200 text-green-800 text-sm">
                                                    <div className="font-medium">Profile Complete</div>
                                                    <div className="text-xs mt-1">Your profile is 100% complete. All features unlocked!</div>
                                                </div>
                                            )}
                                            
                                            {/* Lead notifications */}
                                            {notifications.length > 0 ? (
                                                notifications.map((notification) => (
                                                    <div 
                                                        key={notification.id} 
                                                        className={`p-3 rounded-lg border text-sm ${
                                                            notification.read 
                                                                ? 'bg-gray-50 border-gray-200 text-gray-600' 
                                                                : 'bg-blue-50 border-blue-200 text-blue-800'
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                                            </svg>
                                                            <div className="flex-1">
                                                                <div className="font-medium">{notification.message}</div>
                                                                <div className="text-xs mt-1 opacity-75">
                                                                    {new Date(notification.timestamp).toLocaleString()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="p-3 text-center text-sm text-gray-500">
                                                    No new notifications
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm">
                                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-semibold text-sm overflow-hidden">
                                    {profile?.business?.logo ? (
                                        <img src={profile.business.logo} alt={profile.business.company} className="w-full h-full object-cover" />
                                    ) : (
                                        profile?.business?.company?.[0] || 'M'
                                    )}
                                </div>
                                <span className="hidden sm:inline font-medium text-gray-700 text-sm">{profile?.business?.company || 'Company'}</span>
                            </div>
                            {/* Logout is available in the navbar; removed duplicate page-level button */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Modal */}
            <MoverProfileModal isOpen={showProfile} onClose={() => setShowProfile(false)} profile={profile} />

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-4">
                    <div className="flex gap-4 md:gap-8 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap text-sm md:text-base ${activeTab === 'overview'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="flex items-center gap-2">
                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                </svg>
                                Overview
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('leads')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap text-sm md:text-base ${activeTab === 'leads'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="flex items-center gap-2">
                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                </svg>
                                Leads
                            </span>
                        </button>
                        {!isTeamMember && (
                            <button
                                onClick={() => setActiveTab('business')}
                                className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap text-sm md:text-base ${activeTab === 'business'
                                    ? 'border-orange-500 text-orange-600'
                                    : 'border-transparent text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" />
                                        <path d="M6 8h8v2H6V8zm0 4h8v2H6v-2z" />
                                    </svg>
                                    My Business
                                </span>
                            </button>
                        )}
                        {!isTeamMember && (
                            <button
                                onClick={() => setActiveTab('reviews')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap text-sm md:text-base ${activeTab === 'reviews'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="flex items-center gap-2">
                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                Reviews
                            </span>
                            </button>
                        )}
                        {!isTeamMember && (
                            <button
                                onClick={() => setActiveTab('team')}
                            className={`py-4 px-2 border-b-2 font-medium transition-colors whitespace-nowrap text-sm md:text-base ${activeTab === 'team'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="flex items-center gap-2">
                                <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                </svg>
                                My Team
                            </span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-6 md:py-8">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <>
                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                            {stats.map((stat, index) => (
                                <div
                                    key={index}
                                    className={`${stat.bgColor} rounded-xl md:rounded-2xl p-5 md:p-6 border ${stat.borderColor} shadow-[0_4px_20px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.15)] transition-all duration-300 transform hover:-translate-y-1`}
                                >
                                    <div className="flex items-start justify-between mb-3 md:mb-4">
                                        <div className="flex-1">
                                            <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                                            <p className="text-2xl md:text-3xl font-bold text-gray-800">{stat.value}</p>
                                        </div>
                                        <div className={`${stat.iconColor} w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center bg-white shadow-sm`}>
                                            {stat.icon}
                                        </div>
                                    </div>
                                    <p className="text-xs md:text-sm text-gray-600">{stat.subtitle}</p>
                                </div>
                            ))}
                        </div>

                        {/* My Leads Section */}
                        <div className="bg-white rounded-xl md:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-200 overflow-hidden mb-6 md:mb-8">
                            <div className="p-4 md:p-6 border-b border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        <h2 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                            </svg>
                                            My Leads
                                        </h2>
                                        <p className="text-xs md:text-sm text-gray-600 mt-1">Contact your leads directly via phone</p>
                                    </div>
                                    <button
                                        onClick={() => setActiveTab('leads')}
                                        className="px-3 py-2 text-blue-600 hover:text-blue-700 font-medium text-xs md:text-sm flex items-center gap-1 self-start sm:self-auto">
                                        View All
                                        <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="divide-y divide-gray-200">
                                {leads.length === 0 ? (
                                    <div className="p-8 text-center text-gray-500">
                                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <p className="font-medium text-gray-700 mb-1">No leads yet</p>
                                        <p className="text-sm">Leads assigned to you will appear here.</p>
                                    </div>
                                ) : (
                                    leads.slice(0, 5).map((lead, index) => (
                                        <div key={index} className="p-4 md:p-6 hover:bg-gray-50 transition-colors relative">
                                            {!isProfileComplete && (
                                                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center">
                                                    <div className="text-center p-4">
                                                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-3">
                                                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                            </svg>
                                                        </div>
                                                        <p className="text-sm font-semibold text-gray-900 mb-1">Complete Profile to Unlock</p>
                                                        <p className="text-xs text-gray-600 mb-3">Profile: {completionPercentage}%</p>
                                                        <button
                                                            onClick={() => setActiveTab('business')}
                                                            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded-lg shadow-md transition-colors"
                                                        >
                                                            Complete Now
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            <div className={`flex flex-col gap-4 ${!isProfileComplete ? 'blur-sm pointer-events-none' : ''}`}>
                                                {/* Lead Header */}
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1">
                                                        <p className="text-xs text-gray-500 mb-2">Lead {lead.id}</p>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className={`inline-block px-2.5 py-1 ${lead.statusColor} text-white text-xs font-medium rounded-full`}>
                                                                {lead.status}
                                                            </span>
                                                            <span className="inline-block px-2.5 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">
                                                                {lead.bedrooms}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        disabled={!isProfileComplete}
                                                        className={`px-4 md:px-6 py-2 md:py-2.5 font-medium rounded-lg shadow-md transition-all duration-300 flex items-center gap-2 text-sm whitespace-nowrap ${isProfileComplete ? 'bg-green-500 hover:bg-green-600 hover:shadow-lg text-white' : 'bg-gray-300 cursor-not-allowed text-gray-500'}`}
                                                    >
                                                        <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                                        </svg>
                                                        Call
                                                    </button>
                                                </div>

                                                {/* Lead Details Grid */}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                            </svg>
                                                            Customer
                                                        </p>
                                                        <p className="font-medium text-gray-800 text-sm">{lead.customer}</p>
                                                    </div>

                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                                            </svg>
                                                            Phone
                                                        </p>
                                                        <p className="font-medium text-gray-800 text-sm">{lead.phone}</p>
                                                    </div>

                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                            </svg>
                                                            Location
                                                        </p>
                                                        <p className="font-medium text-gray-800 text-sm">{lead.location}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5">Move Date: {lead.moveDate}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Recent Reviews Section */}
                        <div className="bg-white rounded-xl md:rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-200 overflow-hidden">
                            <div className="p-4 md:p-6 border-b border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        <h2 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <svg className="w-5 h-5 md:w-6 md:h-6 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                            Recent Reviews
                                        </h2>
                                    </div>
                                    <button
                                        onClick={() => setActiveTab('reviews')}
                                        className="px-3 py-2 text-orange-600 hover:text-orange-700 font-medium text-xs md:text-sm flex items-center gap-1 self-start sm:self-auto">
                                        View All Reviews
                                        <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="p-4 md:p-6 space-y-4">
                                {recentReviews.length > 0 ? (
                                    recentReviews.map((review) => (
                                        <div key={review.id} className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg md:rounded-xl p-4 md:p-5 border border-orange-100 shadow-sm hover:shadow-md transition-all duration-300">
                                            <div className="flex items-start gap-3 md:gap-4">
                                                <div className="flex-shrink-0">
                                                    <div className="flex items-center gap-1">
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <svg key={star} className={`w-4 h-4 md:w-5 md:h-5 ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                            </svg>
                                                        ))}
                                                        <span className="ml-1 md:ml-2 text-xs md:text-sm font-bold text-gray-800">{review.rating}</span>
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-gray-700 text-sm md:text-base mb-2 md:mb-3">{review.review_text}</p>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="text-xs md:text-sm font-medium text-gray-800 truncate">{review.reviewer_name || 'Customer'}</p>
                                                        <p className="text-xs text-gray-500 whitespace-nowrap">
                                                            {new Date(review.created_at).toLocaleDateString('en-US', { 
                                                                year: 'numeric', 
                                                                month: 'short', 
                                                                day: 'numeric' 
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                        </svg>
                                        <p className="text-sm">No reviews yet</p>
                                        <p className="text-xs text-gray-400 mt-1">Reviews from customers will appear here</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* Leads Tab */}
                {activeTab === 'leads' && (
                    <div>
                        <div className="mb-4">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Leads</h1>
                        </div>

                        {!isProfileComplete ? (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {/* Left: list */}
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden lg:col-span-1">
                                    <div className="p-3 border-b flex items-center gap-2">
                                        <input
                                            value={leadQuery}
                                            onChange={(e) => setLeadQuery(e.target.value)}
                                            className="flex-1 border rounded-md px-3 py-2 text-sm"
                                            placeholder="Customer, request id..." />
                                        <button
                                            onClick={() => setLeadQuery(leadQuery.trim())}
                                            className="px-3 py-2 bg-orange-500 text-white rounded-md">Search</button>
                                    </div>
                                    <div className="max-h-[70vh] overflow-y-auto divide-y">
                                        {filteredLeads.map((l, i) => (
                                            <button key={i} onClick={() => setSelectedLead(l)} className={`w-full text-left px-3 py-3 hover:bg-gray-50 ${selectedLead?.id === l.id ? 'bg-orange-50' : ''}`}>
                                                <div className="text-xs text-gray-500 mb-1">{l.receivedDate}</div>
                                                <div className="font-semibold text-gray-800 truncate">{l.customer} • {l.location}</div>
                                                <div className="text-xs text-gray-600 truncate">{l.moveType}</div>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="p-3 text-xs text-gray-500 border-t">Showing {filteredLeads.length} of {leads.length} leads</div>
                                </div>

                                {/* Right: detail pane - BLURRED */}
                                <div className="relative bg-white rounded-xl border border-gray-200 overflow-hidden lg:col-span-2 min-h-[60vh]">
                                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center">
                                        <div className="text-center p-8">
                                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-4">
                                                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">Lead Details Locked</h3>
                                            <p className="text-gray-600 mb-4 max-w-md">
                                                Complete your profile to 100% to view detailed lead information.
                                            </p>
                                            <div className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-lg font-medium mb-4">
                                                Profile Completion: {completionPercentage}%
                                            </div>
                                            <div>
                                                <button
                                                    onClick={() => setActiveTab('business')}
                                                    className="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg shadow-md transition-colors"
                                                >
                                                    Complete Profile
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    {selectedLead && (
                                        <div className="p-4 blur-sm pointer-events-none">
                                            {/* Lead Header Info */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                    <div className="text-xs text-blue-600 mb-1">Lead Number</div>
                                                    <div className="font-semibold text-gray-800">{selectedLead.id}</div>
                                                </div>
                                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                                    <div className="text-xs text-green-600 mb-1">Received Date</div>
                                                    <div className="font-semibold text-gray-800">{selectedLead.receivedDate}</div>
                                                </div>
                                            </div>
                                            <div className="border rounded-lg p-4">
                                                <h3 className="font-semibold text-gray-800 mb-3">Customer Information</h3>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <div className="text-xs text-gray-500 mb-1">Name</div>
                                                        <div className="font-medium text-gray-800">{selectedLead.customer}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-gray-500 mb-1">Phone</div>
                                                        <div className="font-medium text-gray-800">{selectedLead.phone}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {/* Left: list */}
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden lg:col-span-1">
                                    <div className="p-3 border-b flex items-center gap-2">
                                        <input
                                            value={leadQuery}
                                            onChange={(e) => setLeadQuery(e.target.value)}
                                            className="flex-1 border rounded-md px-3 py-2 text-sm"
                                            placeholder="Customer, request id..." />
                                        <button
                                            onClick={() => setLeadQuery(leadQuery.trim())}
                                            className="px-3 py-2 bg-orange-500 text-white rounded-md">Search</button>
                                    </div>
                                    <div className="max-h-[70vh] overflow-y-auto divide-y">
                                        {filteredLeads.map((l, i) => (
                                            <button key={i} onClick={() => setSelectedLead(l)} className={`w-full text-left px-3 py-3 hover:bg-gray-50 ${selectedLead?.id === l.id ? 'bg-orange-50' : ''}`}>
                                                <div className="text-xs text-gray-500 mb-1">{l.receivedDate}</div>
                                                <div className="font-semibold text-gray-800 truncate">{l.customer} • {l.location}</div>
                                                <div className="text-xs text-gray-600 truncate">{l.moveType}</div>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="p-3 text-xs text-gray-500 border-t">Showing {filteredLeads.length} of {leads.length} leads</div>
                                </div>

                                {/* Right: detail pane */}
                                <div className="relative bg-white rounded-xl border border-gray-200 overflow-hidden lg:col-span-2 min-h-[60vh]">
                                    {selectedLead ? (
                                        <div className="p-4">
                                            {/* Lead Header Info */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                    <div className="text-xs text-blue-600 mb-1">Lead Number</div>
                                                    <div className="font-semibold text-gray-800">{selectedLead.id}</div>
                                                </div>
                                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                                    <div className="text-xs text-green-600 mb-1">Received Date</div>
                                                    <div className="font-semibold text-gray-800">{selectedLead.receivedDate}</div>
                                                </div>
                                                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                                    <div className="text-xs text-purple-600 mb-1">Move Date</div>
                                                    <div className="font-semibold text-gray-800">{selectedLead.moveDate}</div>
                                                </div>
                                                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                                    <div className="text-xs text-orange-600 mb-1">Status</div>
                                                    <span className={`inline-block px-2.5 py-1 ${selectedLead.statusColor} text-white text-xs font-medium rounded-full`}>
                                                        {selectedLead.status}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="border rounded-lg">
                                                <div className="flex items-center gap-4 border-b px-4 py-2 text-sm">
                                                    <button className="px-3 py-1 rounded-md bg-orange-500 text-white">Details</button>
                                                </div>
                                                {isProfileComplete ? (
                                                    <div className="p-4">
                                                        {/* Customer Information */}
                                                        <div className="mb-6">
                                                            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                                </svg>
                                                                Customer Information
                                                            </h3>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                <div>
                                                                    <div className="text-xs text-gray-500 mb-1">Name</div>
                                                                    <div className="font-medium text-gray-800">{selectedLead.customer}</div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-xs text-gray-500 mb-1">Phone</div>
                                                                    <div className="font-medium text-gray-800">{selectedLead.phone}</div>
                                                                </div>
                                                                <div className="sm:col-span-2">
                                                                    <div className="text-xs text-gray-500 mb-1">Email</div>
                                                                    <div className="font-medium text-gray-800">{selectedLead.email}</div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Move Details */}
                                                        <div className="mb-6">
                                                            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                                                </svg>
                                                                Move Details
                                                            </h3>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                <div>
                                                                    <div className="text-xs text-gray-500 mb-1">Move Type</div>
                                                                    <div className="font-medium text-gray-800">{selectedLead.moveType}</div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-xs text-gray-500 mb-1">Service Type</div>
                                                                    <div className="font-medium text-gray-800">{selectedLead.moveService}</div>
                                                                </div>

                                                                {/* Conditional fields based on service type */}
                                                                {selectedLead.moveService === 'Vehicle Move' && selectedLead.vehicleType && (
                                                                    <>
                                                                        <div>
                                                                            <div className="text-xs text-gray-500 mb-1">Vehicle Type</div>
                                                                            <div className="font-medium text-gray-800">{selectedLead.vehicleType}</div>
                                                                        </div>
                                                                        {selectedLead.vehicleMake && (
                                                                            <div>
                                                                                <div className="text-xs text-gray-500 mb-1">Make & Model</div>
                                                                                <div className="font-medium text-gray-800">
                                                                                    {selectedLead.vehicleMake} {selectedLead.vehicleModel}
                                                                                    {selectedLead.vehicleYear && ` (${selectedLead.vehicleYear})`}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {selectedLead.moveService === 'Move a Piano' && selectedLead.pianoType && (
                                                                    <>
                                                                        <div>
                                                                            <div className="text-xs text-gray-500 mb-1">Piano Type</div>
                                                                            <div className="font-medium text-gray-800">{selectedLead.pianoType}</div>
                                                                        </div>
                                                                        {selectedLead.pianoFloors && (
                                                                            <div>
                                                                                <div className="text-xs text-gray-500 mb-1">Floors/Stairs</div>
                                                                                <div className="font-medium text-gray-800">{selectedLead.pianoFloors}</div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {selectedLead.moveService === 'Truck or Container Rental' && selectedLead.truckSize && (
                                                                    <div>
                                                                        <div className="text-xs text-gray-500 mb-1">Truck Size</div>
                                                                        <div className="font-medium text-gray-800">{selectedLead.truckSize}</div>
                                                                    </div>
                                                                )}

                                                                {(selectedLead.moveService !== 'Vehicle Move' &&
                                                                    selectedLead.moveService !== 'Move a Piano' &&
                                                                    selectedLead.moveService !== 'Truck or Container Rental') &&
                                                                    selectedLead.propertySize && (
                                                                        <div>
                                                                            <div className="text-xs text-gray-500 mb-1">Property Size</div>
                                                                            <div className="font-medium text-gray-800">{selectedLead.propertySize}</div>
                                                                        </div>
                                                                    )}

                                                                <div>
                                                                    <div className="text-xs text-gray-500 mb-1">Distance</div>
                                                                    <div className="font-medium text-gray-800">{selectedLead.distance}</div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Addresses */}
                                                        <div className="mb-6">
                                                            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                                </svg>
                                                                Addresses
                                                            </h3>
                                                            <div className="grid grid-cols-1 gap-4">
                                                                <div>
                                                                    <div className="text-xs text-gray-500 mb-1">From</div>
                                                                    <div className="font-medium text-gray-800 p-2 bg-gray-50 rounded border">{selectedLead.fromAddress}</div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-xs text-gray-500 mb-1">To</div>
                                                                    <div className="font-medium text-gray-800 p-2 bg-gray-50 rounded border">{selectedLead.toAddress}</div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Project Description */}
                                                        <div>
                                                            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                                <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                                                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                                                </svg>
                                                                Description
                                                            </h3>
                                                            <div className="p-3 bg-gray-50 rounded border text-sm text-gray-700">
                                                                {selectedLead.description}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="p-6">
                                                        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm">
                                                            <div className="font-semibold text-gray-800 mb-1">Lead Details Locked</div>
                                                            <div className="text-gray-700">Complete your profile to 100% to view detailed lead information. Currently at {completionPercentage}%.</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-6 text-sm text-gray-600">Select a lead from the list to view details.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* My Business Tab */}
                {activeTab === 'business' && (
                    <BusinessProfileEditable />
                )}

                {/* Reviews Tab */}
                {activeTab === 'reviews' && <Reviews reviews={reviews} moverData={moverData} />}

                {/* My Team Tab */}
                {activeTab === 'team' && <MyTeam moverData={moverData} />}
            </div>

            {/* Pause Account Confirmation Modal */}
            {showPauseModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
                        <div className="text-center">
                            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4 ${
                                moverData?.account_paused ? 'bg-green-100' : 'bg-orange-100'
                            }`}>
                                {moverData?.account_paused ? (
                                    <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                ) : (
                                    <svg className="h-10 w-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                )}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                {moverData?.account_paused ? 'Resume Your Account?' : 'Pause Your Account?'}
                            </h3>
                            <p className="text-gray-600 mb-6">
                                {moverData?.account_paused 
                                    ? 'You will start receiving new leads again when you resume your account.' 
                                    : 'You will not receive any new leads while your account is paused.'}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowPauseModal(false)}
                                    disabled={isPausing}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePauseAccount}
                                    disabled={isPausing}
                                    className={`flex-1 px-4 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50 ${
                                        moverData?.account_paused 
                                            ? 'bg-green-600 hover:bg-green-700' 
                                            : 'bg-orange-600 hover:bg-orange-700'
                                    }`}
                                >
                                    {isPausing ? 'Processing...' : (moverData?.account_paused ? 'Yes, Resume' : 'Yes, Pause')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Popup */}
            {showSuccessPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl animate-scale-in">
                        <div className="text-center">
                            <div className={`mx-auto flex items-center justify-center h-20 w-20 rounded-full mb-4 ${
                                moverData?.account_paused ? 'bg-orange-100' : 'bg-green-100'
                            }`}>
                                {moverData?.account_paused ? (
                                    <svg className="h-12 w-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                ) : (
                                    <svg className="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                )}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                {moverData?.account_paused ? 'Account Paused!' : 'Account Active!'}
                            </h3>
                            <p className="text-gray-600 text-lg">
                                {moverData?.account_paused 
                                    ? 'Your account has been paused. You will not receive any new leads.' 
                                    : 'Your account has been reactivated. You will now receive new leads.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
